// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios';
import {
  acquireToken,
  isAccessTokenExpired,
  isInteractiveAuthInProgress,
} from '../services/auth';
import { cacheRecord, getRecord } from '../services/db';
import {
  cacheData,
  mapApiToDbEntity,
  offlineDelete,
  offlineGet,
  offlinePatch,
  offlinePost,
  offlinePut,
} from '../services/offlineApiHandler';

import { DELETE_RECORD_FROM_DB } from '../store/offline/mutations/mutationTypes';
import onlineService from '../services/onlineService';
import { CONFLICTS_ADD_CONFLICT } from '../store/conflicts/mutations/mutationTypes';

const apiHost = process.env.VUE_APP_API_ENDPOINT;
const FORBIDDEN_TOAST_THROTTLE_MS = 2000;

let lastForbiddenToastAt = 0;

const client = axios.create({
  baseURL: `${apiHost}/dij`,
});

const showForbiddenToast = (error) => {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  if (now - lastForbiddenToastAt < FORBIDDEN_TOAST_THROTTLE_MS) {
    return;
  }

  const toastOpen = window.app?.$buefy?.toast?.open;
  if (typeof toastOpen !== 'function') return;

  lastForbiddenToastAt = now;

  const backendMessage = error?.response?.data?.message;
  const message =
    typeof backendMessage === 'string' && backendMessage.trim().length > 0
      ? backendMessage
      : 'You do not have permission to perform this action.';

  toastOpen({
    duration: 5000,
    message,
    type: 'is-danger',
    position: 'is-bottom',
  });
};

client.injectStore = (store) => {
  client.store = store;
};

const TOOL_PATH_SEGMENTS = ['editor', 'publisher', 'inspector', 'report'];

// Tag requests with the current tool via query param; a custom header is
// rejected by the AWS CORS preflight (Access-Control-Allow-Headers).
client.interceptors.request.use((config) => {
  const toolSegment = window.location.pathname.split('/')[2];
  if (TOOL_PATH_SEGMENTS.includes(toolSegment)) {
    config.params = { tool: toolSegment, ...config.params };
  }
  return config;
});

const onlineGet = client.get;
const onlinePost = client.post;
const onlinePut = client.put;
const onlinePatch = client.patch;
const onlineDelete = client.delete;

const getApiEntityFromUrl = (url = '') => {
  const cleanUrl = (url || '').split('?')[0];
  const parts = cleanUrl.split('/').filter(Boolean);
  return parts[0] || null;
};

const toEntities = (value) => {
  if (Array.isArray(value)) {
    return value.filter(
      (item) => item && typeof item === 'object' && !!item._id
    );
  }

  if (value && typeof value === 'object' && value._id) {
    return [value];
  }

  return [];
};

const cacheMutationData = async (url, responseData, requestBody) => {
  const apiEntity = getApiEntityFromUrl(url);
  const recordType = mapApiToDbEntity(apiEntity);

  if (!recordType) {
    return;
  }

  const responseEntities = toEntities(responseData);
  const requestEntities = toEntities(requestBody);
  const entitiesToCache =
    responseEntities.length > 0 ? responseEntities : requestEntities;

  if (entitiesToCache.length === 0) {
    return;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const entity of entitiesToCache) {
    // eslint-disable-next-line no-await-in-loop
    const existing = await getRecord(recordType, entity._id);
    const merged = existing ? { ...existing, ...entity } : entity;
    const checkedJobId =
      merged.checkedJobId || requestBody?.checkedJobId || null;

    // eslint-disable-next-line no-await-in-loop
    await cacheRecord(recordType, merged, checkedJobId);
  }
};

client.get = async (...args) => {
  if (await onlineService.getStatus()) {
    try {
      const resultPromise = onlineGet(...args);
      try {
        await cacheData(resultPromise, ...args);
      } catch (cacheError) {
        console.error('Error caching data:', cacheError, args);
      }
      return await resultPromise;
    } catch (error) {
      if (!error.response) {
        onlineService.recordNetworkFailure();
        return offlineGet(...args);
      }
      throw error;
    }
  }
  return offlineGet(...args);
};

client.post = async (...args) => {
  if (await onlineService.getStatus()) {
    const response = await onlinePost(...args);
    try {
      await cacheMutationData(args[0], response?.data, args[1]);
    } catch (error) {
      console.error('Error caching mutation data (POST):', error, args);
    }
    return response;
  }
  return offlinePost(...args);
};

client.put = async (...args) => {
  if (await onlineService.getStatus()) {
    const response = await onlinePut(...args);
    try {
      await cacheMutationData(args[0], response?.data, args[1]);
    } catch (error) {
      console.error('Error caching mutation data (PUT):', error, args);
    }
    return response;
  }
  return offlinePut(...args);
};

client.patch = async (...args) => {
  if (await onlineService.getStatus()) {
    const response = await onlinePatch(...args);
    try {
      await cacheMutationData(args[0], response?.data, args[1]);
    } catch (error) {
      console.error('Error caching mutation data (PATCH):', error, args);
    }
    return response;
  }
  return offlinePatch(...args);
};

client.delete = async (...args) => {
  const deleteOperation = (await onlineService.getStatus())
    ? onlineDelete(...args)
    : offlineDelete(...args);

  return deleteOperation
    .then((response) => {
      if (response.status === 200) {
        const url = args[0];

        // If the URL contains 'report/revision', we do not delete from the store
        // This is to handle the deletion of report revisions specifically
        // and avoid removing the entire report from the store.
        // This is necessary because the report revisions are handled differently
        // and we want to keep the report data intact.
        if (url.includes('report/revision')) {
          return response;
        }
        const splittedUrl = url.split('/');
        const recordType = mapApiToDbEntity(splittedUrl[1]);
        client.store.commit(DELETE_RECORD_FROM_DB, {
          recordType,
          recordId: splittedUrl[2],
        });
      }
      return response;
    })
    .catch((error) => {
      throw error;
    });
};

client.interceptors.request.use(
  async (config) => {
    const accessToken = await acquireToken();

    const urls = ['signin', 'callback'];
    const checkToken = !urls.some((url) => document.URL.includes(url));

    if (!checkToken) {
      if (accessToken) {
        config.headers.authorization = `Bearer ${accessToken}`;
      }
      return config;
    }

    if (isInteractiveAuthInProgress()) {
      return Promise.reject(new Error('Authentication redirect in progress'));
    }

    const tokenMissing = !accessToken || isAccessTokenExpired(accessToken);
    const browserOffline =
      typeof navigator !== 'undefined' && navigator.onLine === false;

    if (tokenMissing) {
      if (!browserOffline) {
        window.dispatchEvent(new CustomEvent('showRefreshMessage'));
      }
      return Promise.reject(new Error('Invalid token'));
    }

    config.headers.authorization = `Bearer ${accessToken}`;

    return config;
  },
  (error) => {
    // Handle other types of errors that occur during the request
    return Promise.reject(error);
  }
);

client.interceptors.response.use(
  (response) => {
    onlineService.recordSuccessfulNetworkActivity();
    return response;
  },
  (error) => {
    if (!error.response) {
      onlineService.recordNetworkFailure();
    }

    if (error.response?.status === 403) {
      showForbiddenToast(error);
    }

    const modifiedError = new Error(error.message || 'An error occurred');

    modifiedError.status = error.response?.status || 500;
    modifiedError.message =
      error.response?.data?.message || error.message || 'An error occurred';
    modifiedError.data = error.response?.data?.data || null;

    // Special handling for 409 Conflict (Optimistic Locking)
    if (error.response?.status === 409) {
      console.log('Persisted Data:', error.response.data.persistedData);
      console.log('Attempted Data:', error.response.data.attemptedData);
      console.log('Differences:', error.response.data.differences);
      modifiedError.persistedData = error.response?.data?.persistedData || null;
      modifiedError.attemptedData = error.response?.data?.attemptedData || null;
      modifiedError.differences = error.response?.data?.differences || null;

      if (client.store && !error.config?.silentConflict) {
        client.store.commit(
          CONFLICTS_ADD_CONFLICT,
          {
            localEntity: modifiedError.attemptedData,
            serverEntity: modifiedError.persistedData,
            differences: modifiedError.differences,
          },
          { root: true }
        );
      }
    }

    return Promise.reject(modifiedError);
  }
);

export default client;
